import { Inject, Injectable, NgZone } from '@angular/core';
import {
  IReaderPublication,
  IReaderView,
  IReaderViewOptions,
  IReadingSystemEngineOptions
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import { EpubOcfResourceProvider, IEpubContentDocument, IEpubOcfResourceProvider, IEpubPublication } from '@colibrio/colibrio-reader-framework/colibrio-core-publication-epub';
import { FlipBookRenderer, SinglePageSwipeRenderer } from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-renderer';
import { environment } from 'src/environments/environment';
import { SimpleObfuscation } from './SimpleObfuscation';
import { AsgardDocEpub } from 'src/app/core/documents/asgard-doc-epub';
import { DocumentManagerService } from '../../document/document-manager.service';
import { ReadingService } from '../../text/reading.service';
import { ExtractedXHTML, extractXHTML } from 'src/app/core/epub-utils/xhtml-extractor';
import { ReadingSystemEngine } from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-engine';
import { IEpubReaderPublication, EpubFormatAdapter } from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub';
import { SettingsService } from '../../settings/settings.service';
import { ToastrDialogService } from '../../toastr-dialog.service';
import { EpubAnnotationService } from '../../epub/epub-annotations.service';
import { CurrentUiStoreService } from '../../ui/current-ui-store.service';
import { ReadingModesService } from '../../reading-modes.service';
import { EPubAnnotationServiceToken, IEpubAnnotationService } from '../../epub/base-epub-annotation.service';
import { AutosaveManagerService } from '../../autosave/autosave-manager.service';
import { TabService } from '../../tab.service';
import { FileHashManager } from '../../autosave/file-hash-manager';
import { CurrentUIState } from 'src/models/UIState';
import { Observable } from 'rxjs';
import { RendererName } from 'src/app/core/bottom-bar/bottom-bar-epub';
import { TranslateService } from '@ngx-translate/core';

export interface EpubViewerInstance {
  doc: AsgardDocEpub;
  readingSystemEngine: ReadingSystemEngine;
  readerView: IReaderView;
  readerPublication: IEpubReaderPublication;
  extractedHTMLList: ExtractedXHTML[];
  epubFile: File;
  bottomBarState?: Observable<CurrentUIState>;
}

@Injectable({
  providedIn: 'root'
})
export class EpubInstanceManagerService {
  private readonly instanceStorage: Map<string, EpubViewerInstance> = new Map();

  constructor(
    private readonly docMgrService: DocumentManagerService,
    private readonly readingService: ReadingService, // for epub tts
    private readonly settingsService: SettingsService, // for readingType in text iterator
    private readonly toastrService: ToastrDialogService,
    private readonly annotationsService: EpubAnnotationService,
    private readonly uiStore: CurrentUiStoreService,
    private readonly ngZone: NgZone, // for click and read
    private readonly readingModeService: ReadingModesService, // for click and read
    private readonly autosave: AutosaveManagerService,
    @Inject(EPubAnnotationServiceToken)
    private readonly allAnnotationServices: IEpubAnnotationService[], // for save and autosave
    private readonly tabService: TabService,
    private readonly translate: TranslateService
  ) {
    this.docMgrService.documentRemoved.subscribe(async id => {
      if (id) {
        this.annotationsService.destroyAnnotationLayers(id);
        await this.destroy(id);
      }
    });
  }

  async createViewerInstance(viewElement: HTMLElement, epubFile: File, doc: AsgardDocEpub) {
    const readingSystemEngine = await this.loadEngine();
    const readerView = this.setupReaderView(readingSystemEngine);
    const newOptions: IReaderViewOptions = {
                emitPointerMoveEventsOnHover: true,
                gestureOptions: {
                    panZoom: {
                        pointerTypes: {
                            mouse: true,
                            pen: true,
                            touch: true,
                        }
                    }
                }
            }
    readerView.setOptions(newOptions);

    // Tell the ReaderView which element to use for rendering. Note that the ReaderView will not render anything yet,
    // since we haven't even loaded a publication yet or told it which page to render.
    readerView.renderTo(viewElement);

    // To load the EPUB file you first create a resource provider. A resource provider helps you get the resources
    // of a publication but hides the details of the internal structure. To get the resources from a file with
    // the EPUB Open Container Format (OCF) create a corresponding resource provider.
    const ocfResourceProvider: IEpubOcfResourceProvider = await EpubOcfResourceProvider.createFromBlob(epubFile);

    // An OCF container can host multiple EPUB publications where the first publication defined in the container.xml is the default.
    // A publication contains the documents and metadata of the EPUB file.
    const epubPublication: IEpubPublication | null = ocfResourceProvider.getDefaultPublication();
    if (epubPublication === null) {
      console.error('No publication found in this EPUB file.');
      return;
    }

    // extract the xhtml files
    const extractedHTMLList = await this.extractHTML(ocfResourceProvider, epubPublication);

    // load the epub
    const readerPublication = await this.loadEpub(epubPublication, readingSystemEngine, readerView);

    // store the new instance
    const inst: EpubViewerInstance = {
      doc,
      readingSystemEngine,
      readerView,
      readerPublication,
      extractedHTMLList,
      epubFile
    };
    this.instanceStorage.set(doc.documentId, inst)
    this.docMgrService.setDocumentOptins(doc.documentId, [inst]);
    this.annotationsService.initAnnotationLayers(doc.documentId);
    doc.docHash = await FileHashManager.calculateFileHash(epubFile);
    await this.autosave.addOriginalFile(doc.docHash, epubFile, epubFile.name, null);
    await doc.onInstanceReady(
      this.settingsService,
      this.readingService,
      this.toastrService,
      this.uiStore,
      this.ngZone,
      this.readingModeService,
      this.allAnnotationServices,
      this.autosave,
      this.tabService,
      this.translate
    );
  }

  cleanOndestroy(): void {
    this.instanceStorage.forEach(async (_value, key) => await this.destroy(key));
    this.instanceStorage.clear();
  }

  getInstance(id: string): EpubViewerInstance | null {
    return this.instanceStorage.get(id) ?? null;
  }

  // TODO find a better way
  passBottomBarState(id: string, currentUIState: Observable<CurrentUIState>): void {
    const inst = this.instanceStorage.get(id);
    if (inst) inst.bottomBarState = currentUIState;
  }

  private async loadEngine(): Promise<ReadingSystemEngine> {
    // Options for the ReadingSystemEngine. The only required one is licenseApiKey, and it is important that it is
    // set correctly to the license key you have received from Colibrio.
    const readingSystemOptions: IReadingSystemEngineOptions = {
      licenseApiKey: environment.colibrioLicenceKey
    };
    // The ReadingSystemEngine is the main entry point for the Colibrio reading system.
    const readingSystemEngine = new ReadingSystemEngine(readingSystemOptions);

    // Add support for EPUBs to the reading system. The reason you have to
    // add the format adapter manually is that the format implementations can be quite big, so we only use what we need.
    readingSystemEngine.addFormatAdapter(new EpubFormatAdapter());
    return readingSystemEngine;
  }

  private setupReaderView(readingSystemEngine: ReadingSystemEngine): IReaderView {
    // A ReaderView is used for rendering and navigating in a publication.
    const readerView = readingSystemEngine.createReaderView();

    // Before the ReaderView can render anything, it needs at least one renderer. We choose the FlipBookRenderer for
    // this example, but any renderer will work just fine.
    readerView.addRenderer(new FlipBookRenderer({name: RendererName.FLIP_BOOK}));
    const singlePageSwipeRenderer = new SinglePageSwipeRenderer({name: RendererName.SINGLE_PAGE_SWIPE});
    readerView.addRenderer(singlePageSwipeRenderer);
    // NOTE
    // make sure the default in the bottombar is the same one!
    readerView.setActiveRenderer(singlePageSwipeRenderer);
    return readerView;
  }

  private async extractHTML(ocfResourceProvider: IEpubOcfResourceProvider, epubPublication: IEpubPublication): Promise<ExtractedXHTML[]> {
    // retrieve the spine so we can get the xhtml for every item in the spine to send to cheshire cat
    const spine: IEpubContentDocument[] = epubPublication.getSpine();
    return await extractXHTML(spine, ocfResourceProvider);
  }

  private async loadEpub(epubPublication: IEpubPublication, readingSystemEngine: ReadingSystemEngine, readerView: IReaderView): Promise<IEpubReaderPublication> {
    // The user and publication token is a way to keep track of the usage according to your pricing model. The tokens are sent
    // to a license server. Make them unique and avoid sending sensitive data, e.g. by hashing the tokens.
    // TODO
    const userId = "sensotec";
    const licenseOptions = {
      userToken: await SimpleObfuscation.obfuscate(userId),
      publicationToken: await SimpleObfuscation.obfuscate(epubPublication.getHashSignature())
    };
    // To create a version of the publication that are processed to be rendered and searched, you can load the publication
    // retrieved by the resource provider. The readingSystemEngine finds the proper format adapter and creates a reader publication.
    const readerPublication: IReaderPublication = await readingSystemEngine.loadPublication(epubPublication, undefined, licenseOptions);

    // The spine is the ordered list of documents in the publication. Set the spine as the reader view's
    // active documents to render all documents in the ReaderPublication
    readerView.setReaderDocuments(readerPublication.getSpine());

    // The ReaderView must be instructed what to render, in this case the start of the publication.
    readerView.goToStart();
    return readerPublication as IEpubReaderPublication;
  }

  private async destroy(documentId: string | null): Promise<void> {
    if (documentId === null) {
      return;
    }
    const asgInst = this.instanceStorage.get(documentId);

    if (!asgInst) {
      return;
    }

    asgInst.doc.onInstanceDestroy();
    //To avoid using unnecessary memory usage, unload any loaded publications.
    asgInst.readerView.setReaderDocuments([]);
    await asgInst.readingSystemEngine.unloadPublication(asgInst.readerPublication);

    this.instanceStorage.delete(documentId);
    console.log('destroy called for', documentId);
  }
}
